import { useState } from "react";
import { createUser } from "../services/userService";
import { getErrorMessage } from "../utils/api/errorUtils";

type UserRoleLabel = "Admin" | "Inspector" | "Owner" | "Lender" | "GC" | "Architect";
type UserStatusLabel = "Active" | "Pending" | "Inactive";
type UserRoleApi = "admin" | "inspector" | "owner" | "lender" | "gc" | "architect";
type UserStatusApi = "active" | "pending_invite" | "inactive";

const ROLE_API_MAP: Record<UserRoleLabel, UserRoleApi> = {
  Admin: "admin",
  Inspector: "inspector",
  Owner: "owner",
  Lender: "lender",
  GC: "gc",
  Architect: "architect",
};

const STATUS_API_MAP: Record<UserStatusLabel, UserStatusApi> = {
  Active: "active",
  Pending: "pending_invite",
  Inactive: "inactive",
};

export interface CreateUserFormState {
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRoleLabel[];
  company: string;
  status: UserStatusLabel;
  phone: string;
  address?: string;
  bio: string;
  designation?: string;
  focused?: string[];
  branchId?: string;
  projects?: string[];
}

interface CreateUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRoleApi[];
  companyName: string;
  status: UserStatusApi;
  phone?: string;
  address?: string;
  bio?: string;
  designation?: string;
  focused?: string[];
  lenderId?: string;
  projectIds?: string[];
}

type CreateUserResult =
  | { success: true; user: Record<string, unknown> }
  | { success: false; error: string };

interface ApiError {
  message?: string;
}

const useCreateUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreateUser = async (formState: CreateUserFormState): Promise<CreateUserResult> => {
    setLoading(true);
    setError("");

    const payload: CreateUserPayload = {
      email: formState.email.trim(),
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
      roles: formState.roles.map((r) => ROLE_API_MAP[r]),
      companyName: formState.company.trim(),
      status: STATUS_API_MAP[formState.status] ?? "active",
    };
    // PhoneInput emits only the dial code (e.g. "+1") when no number is entered
    const phone = formState.phone.trim();
    if (phone.replace(/\D/g, "").length > 3) payload.phone = phone;
    if (formState.address?.trim()) payload.address = formState.address.trim();
    if (formState.bio.trim()) payload.bio = formState.bio.trim();
    if (formState.roles.includes("Inspector")) {
      if (formState.designation) payload.designation = formState.designation;
      if (formState.focused?.length) payload.focused = formState.focused;
    }
    if (formState.branchId) payload.lenderId = formState.branchId;
    if (
      formState.roles.every((r) => r === "Owner" || r === "Architect") &&
      formState.projects?.length
    ) {
      payload.projectIds = formState.projects;
    }

    const { data, error: apiError } = await createUser(payload) as {
      data: { data: { user: Record<string, unknown> } };
      error: ApiError | null;
    };

    setLoading(false);

    if (apiError) {
      const msg = getErrorMessage(apiError, "Failed to create user.");
      setError(msg);
      return { success: false, error: msg };
    }

    return { success: true, user: data.data.user };
  };

  return { handleCreateUser, loading, error };
};

export default useCreateUser;
